import { FC, useState, useEffect } from 'react';
import { IDocumentCreatorProps } from '../../../../@types/documents';
import {
    IActDisassemblingCreateDTO,
    IActDisassemblingGetDTO,
    IActDisassemblingUpdateDTO,
} from '../../../../libs/coreapi-dto/documents/actDisassembling';
import {
    IActDisassemblingItemEditDTO,
    IActDisassemblingItemGetDTO,
} from '../../../../libs/coreapi-dto/documents/actDisassemblingItem';
import { ActDisassemblingDataProvider } from '../../../../Services/DataProviders/ActDisassemblingDataProvider';
import { useAppContext } from '../../../../system/providers/appContextProvider';
import { useTabsContext } from '../../../../system/providers/tabsProvider';
import { ActDisassemblingUpdateForm } from './ActDisassemblingUpdateForm';
import { v4 as uuidv4 } from 'uuid';
import { getActDisassemblingItemUpdateDTO } from '../../../../libs/core-api-requests/customRequest/ActDisassemblingItemUpdateRequest';
import { DateTime } from 'luxon';
import { useUserContext } from '../../../../system/providers/userContextProvider';
import { DocumentOperationSpinner } from '../../../../components/DocumentOperationSpinner/DocumentOperationSpinner';
import { reject } from 'lodash';
import { resolve } from 'mathjs';

interface IEditDocument {
    actDisassemblingDocument: IActDisassemblingGetDTO
    actDisassemblingDocumentItem: IActDisassemblingItemGetDTO[]
    documentUpdate: IActDisassemblingUpdateDTO
}

interface ICopyDocument {
    actDisassemblingDocument: IActDisassemblingGetDTO
    documentItemsUpdate: IActDisassemblingItemEditDTO[]
    documentUpdate: IActDisassemblingUpdateDTO
}

const ActDisassemblingCreatorView: FC<IDocumentCreatorProps<IActDisassemblingCreateDTO>> = (props) => {
    const appCtx = useAppContext();
    const userContext = useUserContext()
    const tabsContext = useTabsContext();

    const actDisassemblingDP = new ActDisassemblingDataProvider(appCtx.coreApiService);

    const [viewState, setViewState] = useState<GridStateType>(props.variant);
    const [id, setId] = useState<string | undefined>(props.idGlobal);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [editDocument, setEditDocument] = useState<IEditDocument | null>(null);
    const [copyDocument, setCopyDocument] = useState<ICopyDocument | null>(null);

    useEffect(() => {
        const initializeComponent = async () => {
            setIsLoading(true)
            
            if (viewState === 'edit') {
                try {
                    await new Promise((resolve, reject) => {
                        actDisassemblingDP.getById(id as string, (ActDisassemblingDocument) => {
                            getActDisassemblingItemUpdateDTO(ActDisassemblingDocument.idGlobal as string, (e) => {
                                const documentUpdate: IActDisassemblingUpdateDTO = {
                                    idGlobal: ActDisassemblingDocument.idGlobal as string,
                                    documentDate: ActDisassemblingDocument.documentDate.toF3DateTimeServerSide(userContext),
                                    idStoreGlobal: ActDisassemblingDocument.store.idGlobal,
                                    comment: ActDisassemblingDocument.comment,
                                    idUserGlobal: undefined,
                                    idGosContractGlobal: ActDisassemblingDocument?.gosContract?.idGlobal ?? undefined,
                                    isNewBarcode: ActDisassemblingDocument.isNewBarcode,
                                    items: e,
                                }
                                const documentItemUpdate: IActDisassemblingItemGetDTO[] = ActDisassemblingDocument.items.map((x) => ({
                                        ...x,
                                        isGnvls: e.find((y) => y.idGlobal === x.idGlobal)?.isGnvls ?? false,
                                        kizCount: e.find((y) => y.idGlobal === x.idGlobal)?.kizs?.length,
                                        kizBoxCount: e.find((y) => y.idGlobal === x.idGlobal)?.kizBoxes?.length,
                                    } as IActDisassemblingItemGetDTO)) as IActDisassemblingItemGetDTO[];
                                setEditDocument({
                                    actDisassemblingDocument: ActDisassemblingDocument,
                                    actDisassemblingDocumentItem: documentItemUpdate,
                                    documentUpdate: documentUpdate,
                                });
                                resolve(null)
                            });
                        });
                    });
    
                } catch(error) {
                    console.error('Error loading document:', error);
                } finally {
                    setIsLoading(false);
                }
            }
    
            else if (viewState === 'copy') {
                try {
                    await new Promise((resolve, reject) => {
                        actDisassemblingDP.getById(id as string, (ActDisassemblingDocument) => {
                            let idGlobal = uuidv4()
                            const documentItemsUpdate: IActDisassemblingItemEditDTO[] = []
                            const documentUpdate: IActDisassemblingUpdateDTO = {
                                idGlobal: idGlobal,
                                documentDate: DateTime.now().toF3DateTimeServerSide(userContext),
                                items: documentItemsUpdate,
                                isNewBarcode: false
                            };
                            setCopyDocument({
                                actDisassemblingDocument: {
                                    idGlobal: idGlobal,
                                    items: [] as IActDisassemblingItemGetDTO[]
                                } as IActDisassemblingGetDTO,
                                documentItemsUpdate: documentItemsUpdate,
                                documentUpdate: documentUpdate
                            });
                            resolve(null)
                        });
                    });
                } catch(error) {
                    console.error('Error loading document:', error);
                } finally {
                    setIsLoading(false);
                }
            } else {
                setIsLoading(false)
            }
        }
        setIsLoading(false);       
        initializeComponent();
    }, [viewState]);

    if (isLoading) {
            return (
                <div >
                    <DocumentOperationSpinner xl data-testid="invoice-creator-loading" />
                </div>
            );
        }

    return (
        <>
            {
                viewState === 'create' ?
                    <ActDisassemblingUpdateForm
                        isSubmitting={isSubmitting}
                        document={null}
                        documentItems={null}
                        documentUpdate={null}
                        idGlobal={props.idGlobal}
                        variant="create"
                        save={(ActDisassembling: IActDisassemblingCreateDTO) => {
                            setIsSubmitting(true);
                            actDisassemblingDP.create(ActDisassembling, (e) => {
                                setId(e);
                                setViewState('edit');
                                props.createdCallback?.();
                                setIsSubmitting(false);
                            }, ()=>{}, (e)=>{
                                setIsSubmitting(false);
                            });
                        }}
                        ok={(ActDisassembling: IActDisassemblingCreateDTO) => {
                            setIsSubmitting(true);
                            actDisassemblingDP.create(ActDisassembling, (e) => {
                                setId(id);
                                props.createdCallback?.();
                                tabsContext.currentTab && tabsContext.close(tabsContext.currentTab);
                                setIsSubmitting(false);
                            }, ()=>{}, (e)=>{
                                setIsSubmitting(false);
                            });
                        }}
                        cancel={() => tabsContext.currentTab && tabsContext.close(tabsContext.currentTab)}
                        permission={props.permission}
                    />
                    :
                    viewState === 'edit' ?
                        editDocument && <ActDisassemblingUpdateForm
                            isSubmitting={isSubmitting}
                            variant="edit"
                            save={(ActDisassembling: IActDisassemblingUpdateDTO) => {
                                setIsSubmitting(true);
                                actDisassemblingDP.update(id as string, ActDisassembling, (e) => {
                                    if (e.respType === 'isFailed') return setIsSubmitting(false);

                                    props.createdCallback?.();
                                    setIsSubmitting(false);
                                });
                            }}
                            ok={(ActDisassembling: IActDisassemblingUpdateDTO) => {
                                setIsSubmitting(true);
                                actDisassemblingDP.update(id as string, ActDisassembling, (e) => {
                                    if (e.respType === 'isFailed') return setIsSubmitting(false);

                                    props.createdCallback?.();
                                    tabsContext.currentTab && tabsContext.close(tabsContext.currentTab);
                                    setIsSubmitting(false);
                                });
                            }}
                            cancel={() => tabsContext.currentTab && tabsContext.close(tabsContext.currentTab)}
                            document={editDocument.actDisassemblingDocument}
                            documentItems={editDocument.actDisassemblingDocumentItem}
                            documentUpdate={editDocument.documentUpdate}
                            permission={props.permission}
                        />
                        :
                        viewState === 'copy' ?
                            copyDocument && <ActDisassemblingUpdateForm
                                isSubmitting={isSubmitting}
                                variant="copy"
                                save={(ActDisassembling: IActDisassemblingUpdateDTO) => {
                                    setIsSubmitting(true);
                                    actDisassemblingDP.create(ActDisassembling, (e) => {
                                        setId(e);
                                        setViewState('edit');
                                        props.createdCallback?.();
                                        setIsSubmitting(false);
                                    }, ()=>{}, (e)=>{
                                        setIsSubmitting(false);
                                    });
                                }}
                                ok={(ActDisassembling: IActDisassemblingUpdateDTO) => {
                                    setIsSubmitting(true);
                                    actDisassemblingDP.create(ActDisassembling, (e) => {
                                        setId(e);
                                        setViewState('create');
                                        props.createdCallback?.();
                                        tabsContext.currentTab && tabsContext.close(tabsContext.currentTab);
                                        setIsSubmitting(false);
                                    }, ()=>{}, (e)=>{
                                        setIsSubmitting(false);
                                    });
                                }}
                                cancel={() => tabsContext.currentTab && tabsContext.close(tabsContext.currentTab)}
                                document={{ ...copyDocument.actDisassemblingDocument, user: undefined }}
                                documentItems={copyDocument.actDisassemblingDocument.items}
                                documentUpdate={copyDocument.documentUpdate}
                                permission={props.permission}
                            />
                            :
                            <></>
            }
        </>
    );
};

export default ActDisassemblingCreatorView;